import { useEffect, useState } from 'react'
import { Betrag } from './Betrag'
import { t } from './sprache'

/**
 * Mehrere Wertzellen markieren, unten rechts steht ihre Summe (7.3 Abschnitt
 * 25, R1016).
 *
 * **Der Auftrag vom 2026-10-04:** *„Ich möchte eine Funktion haben, bei der
 * Anwender mehrere Wertzellen mit gedrückter linker Maustaste markieren
 * können. Unten rechts soll dann die Summe angezeigt werden."* — wie in einer
 * Tabellenkalkulation.
 *
 * **Einmal gebaut, für jeden Bericht.** Markierbar ist jede Zelle, die ihren
 * Betrag als `data-wert` trägt; das tun die beiden Bauteile, die in Bilanz,
 * GuV, Kapitalflussrechnung und den Spiegeln eine Zahl zeichnen. Ein neuer
 * Bericht, der sie benutzt, ist ohne Zutun dabei.
 *
 * **Gerechnet wird mit dem Betrag, der dasteht** — in der Anzeigewährung,
 * nicht mit dem gelesenen Text: Ein „1.234,56" oder ein Haben-Betrag ohne
 * Minus wäre als Text eine andere Zahl.
 */

export const MARKIERT = 'markiert'

/** Die Spalte einer Zelle, über Zellen hinweg gezählt, die mehrere Spalten überspannen. */
function spalteVon(zelle: HTMLTableCellElement): number {
  let spalte = 0
  for (let davor = zelle.previousElementSibling; davor; davor = davor.previousElementSibling) {
    spalte += (davor as HTMLTableCellElement).colSpan || 1
  }
  return spalte
}

/**
 * Die Wertzellen im Rechteck zwischen zwei Zellen derselben Tabelle — so, wie
 * eine Tabellenkalkulation markiert.
 */
export function zellenImRechteck(anker: HTMLTableCellElement, ende: HTMLTableCellElement): HTMLTableCellElement[] {
  const tabelle = anker.closest('table')
  if (!tabelle || ende.closest('table') !== tabelle) {
    return [anker]
  }

  const zeilen = [...tabelle.rows]
  const z1 = zeilen.indexOf(anker.parentElement as HTMLTableRowElement)
  const z2 = zeilen.indexOf(ende.parentElement as HTMLTableRowElement)
  const [oben, unten] = z1 <= z2 ? [z1, z2] : [z2, z1]
  const s1 = spalteVon(anker)
  const s2 = spalteVon(ende)
  const [links, rechts] = s1 <= s2 ? [s1, s2] : [s2, s1]

  return zeilen.slice(oben, unten + 1).flatMap((zeile) =>
    [...zeile.cells].filter((zelle) => {
      if (zelle.dataset.wert === undefined) {
        return false
      }
      const spalte = spalteVon(zelle)
      return spalte >= links && spalte <= rechts
    }),
  )
}

/**
 * Summe und Anzahl — die Summe auf den Cent gerundet, damit sich
 * Gleitkommareste nicht zeigen. **Gezählt werden die Zellen, in denen etwas
 * steht:** Eine leere Zelle ist im Bericht null und am Bildschirm nichts.
 */
export function summeVon(zellen: HTMLTableCellElement[]): { summe: number; anzahl: number } {
  const werte = zellen.map((z) => Number(z.dataset.wert) || 0)
  const summe = werte.reduce((s, w) => s + w, 0)
  return { summe: Math.round(summe * 100) / 100, anzahl: werte.filter((w) => w !== 0).length }
}

/**
 * Hört an der ganzen Seite: Drücken auf eine Wertzelle setzt den Anker,
 * Ziehen spannt das Rechteck auf, Umschalt-Klick ebenso. Ein Klick daneben,
 * Esc oder ein Ortswechsel heben die Markierung auf.
 */
export function useZellmarkierung(): { summe: number; anzahl: number } | null {
  const [stand, setStand] = useState<{ summe: number; anzahl: number } | null>(null)

  useEffect(() => {
    let anker: HTMLTableCellElement | null = null
    let ziehen = false
    let markiert: HTMLTableCellElement[] = []

    function setze(zellen: HTMLTableCellElement[]) {
      markiert.forEach((z) => z.classList.remove(MARKIERT))
      // **Eine Zelle allein ist keine Markierung** — sie wird angeklickt, um
      // die Zahl zu kopieren oder den Nachweis zu öffnen, und soll dabei nicht
      // aufleuchten.
      markiert = zellen.length > 1 ? zellen : []
      markiert.forEach((z) => z.classList.add(MARKIERT))
      setStand(markiert.length > 1 ? summeVon(markiert) : null)
    }

    const zelleVon = (ziel: EventTarget | null) =>
      ziel instanceof Element ? ziel.closest<HTMLTableCellElement>('td[data-wert]') : null

    function runter(e: MouseEvent) {
      if (e.button !== 0) {
        return
      }
      const zelle = zelleVon(e.target)
      if (!zelle) {
        if (!(e.target instanceof Element && e.target.closest('.zellsumme'))) {
          anker = null
          setze([])
        }
        return
      }
      if (e.shiftKey && anker && anker.isConnected && zelle.closest('table') === anker.closest('table')) {
        e.preventDefault()
        setze(zellenImRechteck(anker, zelle))
        return
      }
      anker = zelle
      ziehen = true
      setze([])
    }

    function ueber(e: MouseEvent) {
      if (!ziehen || !anker) {
        return
      }
      const zelle = zelleVon(e.target)
      if (!zelle || zelle === anker || zelle.closest('table') !== anker.closest('table')) {
        return
      }
      // Gezogen wird über Zellen, nicht über Text: Was der Browser dabei als
      // Textauswahl aufspannen würde, fällt weg.
      document.body.classList.add('zellenziehen')
      window.getSelection()?.removeAllRanges()
      setze(zellenImRechteck(anker, zelle))
    }

    function hoch() {
      ziehen = false
      document.body.classList.remove('zellenziehen')
    }

    function taste(e: KeyboardEvent) {
      if (e.key === 'Escape' && markiert.length > 0) {
        setze([])
      }
    }

    function fort() {
      anker = null
      setze([])
    }

    document.addEventListener('mousedown', runter)
    document.addEventListener('mouseover', ueber)
    document.addEventListener('mouseup', hoch)
    document.addEventListener('keydown', taste)
    window.addEventListener('hashchange', fort)
    return () => {
      document.removeEventListener('mousedown', runter)
      document.removeEventListener('mouseover', ueber)
      document.removeEventListener('mouseup', hoch)
      document.removeEventListener('keydown', taste)
      window.removeEventListener('hashchange', fort)
      setze([])
    }
  }, [])

  return stand
}

/** Unten rechts in der Fußzeile: Summe und Anzahl der markierten Werte. */
export function Zellsumme({ stand }: { stand: { summe: number; anzahl: number } | null }) {
  if (!stand) {
    return null
  }

  return (
    <div className="zellsumme" role="status">
      <span className="beschriftung">{t('zellsumme.summe')}</span>{' '}
      <Betrag wert={stand.summe} />
      <span className="zellsumme-anzahl">{t('zellsumme.anzahl', { anzahl: String(stand.anzahl) })}</span>
    </div>
  )
}
